import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { useToaster } from '../../shared/composables/use-toaster';
import { UISocketDataMessage } from '../../shared/models/UISocket';
import { SocketAction, SocketActionHandler } from '../../shared/services/SocketActionHandler';
import { CurrentSiteStore } from '../../site-detail/current-site.store';
import { Site, SiteId } from '../../site-detail/models/Site';
import { getOwnerFullName } from '../../site-owner/utils/owner.util';
import { InterventionsStore } from '../interventions.store';

interface InterventionEventMessage extends UISocketDataMessage {
    interventionSessionId: string;
    siteId: SiteId;
}

interface InterventionClosedMessage extends InterventionEventMessage {
    /** Only sent when the client ended the intervention, not the installer. */
    closedBy?: { firstname: string; lastname: string };
}

/** Reloads the installation's interventions, then calls `known` when the event's intervention is one of them. */
function whenReloaded(interventions: InterventionsStore, message: InterventionEventMessage, known: () => void): void {
    void interventions.load(message.siteId).then(
        (loaded) => {
            if (loaded.some((i) => i.sessionId === message.interventionSessionId)) known();
        },
        () => undefined,
    );
}

/** Calls `found` with the installation, fetched when it isn't the current one; nothing when it can't be found. */
function withSite(sites: CurrentSiteStore, siteId: SiteId, found: (site: Site) => void): void {
    void sites.find(siteId).then(
        (site) => site && found(site),
        () => undefined,
    );
}

/** The client granted access: a toast offers to open the installation, until it is dismissed. */
@Injectable()
export class InterventionGrantedHandler implements SocketActionHandler {
    readonly action: SocketAction = 'interventionAccessGranted';

    private readonly interventions = inject(InterventionsStore);
    private readonly sites = inject(CurrentSiteStore);
    private readonly router = inject(Router);
    private readonly translate = inject(TranslateService);
    private readonly toaster = useToaster();

    handle(message: InterventionEventMessage): void {
        whenReloaded(this.interventions, message, () =>
            withSite(this.sites, message.siteId, (site) => this.offerToStart(site)),
        );
    }

    private offerToStart(site: Site): void {
        this.toaster.success({
            title: this.translate.instant('TOASTER_ACCESS_GRANTED_HEADER', {
                userName: getOwnerFullName(site.owners[0]),
            }),
            action: {
                label: this.translate.instant('TOASTER_ACCESS_GRANTED_START_INTERVENTION'),
                run: () => this.router.navigate([`/gateway/${site.gatewayId}`]),
            },
            duration: 0,
        });
    }
}

@Injectable()
export class InterventionRefusedHandler implements SocketActionHandler {
    readonly action: SocketAction = 'interventionAccessRefused';

    private readonly interventions = inject(InterventionsStore);
    private readonly sites = inject(CurrentSiteStore);
    private readonly translate = inject(TranslateService);
    private readonly toaster = useToaster();

    handle(message: InterventionEventMessage): void {
        whenReloaded(this.interventions, message, () =>
            withSite(this.sites, message.siteId, (site) => this.tellRefused(site)),
        );
    }

    private tellRefused(site: Site): void {
        this.toaster.info({
            title: this.translate.instant('TOASTER_INTERVENTION_REFUSED_BY_ENDUSER_HEADER', {
                userName: getOwnerFullName(site.owners[0]),
            }),
        });
    }
}

@Injectable()
export class InterventionClosedHandler implements SocketActionHandler {
    readonly action: SocketAction = 'interventionClosed';

    private readonly interventions = inject(InterventionsStore);
    private readonly translate = inject(TranslateService);
    private readonly toaster = useToaster();

    handle(message: InterventionClosedMessage): void {
        whenReloaded(this.interventions, message, () => this.tellEndedByClient(message.closedBy));
    }

    private tellEndedByClient(closedBy: InterventionClosedMessage['closedBy']): void {
        if (!closedBy) return;

        this.toaster.info({ title: this.translate.instant('TOASTER_INTERVENTION_ENDED_BY_ENDUSER_HEADER', closedBy) });
    }
}
